import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
} from "antd";
import { PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import GuidedTour, { hasTourFlag } from "../common/GuidedTour/GuidedTour";
import moment from "moment";
import Service from "../../service";
import { useDispatch } from "react-redux";
import { hideAuthLoader, showAuthLoader } from "../../appRedux/actions/Auth";
import {
  expenseApi,
  fetchEmployeeOptions,
  formatMoney,
  getExpenseCurrencySymbol,
  setExpenseCurrencySymbol,
  statusTag,
} from "./expenseHelpers";
import { getResource } from "../../util/getResourcePermission";
import { getRoles } from "../../util/getRoles";
import { PAGE_SIZE_OPTIONS } from "../../constants/tablePagination";
import NodataFoundIcon from "../../assets/images/NodataFoundIcon";
import config from "../../permission/config.json";
import "./expenseManagement.css";
import useCompanyDateFormat from "../../hooks/useCompanyDateFormat";
import EmpNameCodeCell from "../common/EmpNameCodeCell";

const { TextArea } = Input;
const { Option } = Select;

// Fallback role lists used until the company config loads (or if it's unset).
const DEFAULT_ISSUER_ROLES = ["HR", "Account Team", "Reporting Manager"];
const DEFAULT_SETTLER_ROLES = ["Account Team"];

const TOUR_STORAGE_KEY = "tour_seen_advance_request";
const TOUR_HINT_STORAGE_KEY = "tour_hint_dismissed_advance_request";
// Steps whose target field lives inside the Issue Advance modal - the tour
// opens that real modal itself when it reaches one of these.
const MODAL_STEP_IDS = [
  "ar-employee",
  "ar-amount",
  "ar-cost-center",
  "ar-purpose",
  "ar-project-client",
  "ar-dates",
  "ar-remarks",
  "ar-issue-btn",
];

// Advance Request page - issue advances (HR / Account Team / RM / Admin) and
// view a role-scoped list of advances with settle actions.
const AdvanceRequest = () => {
  const dateFormat = useCompanyDateFormat();
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [advances, setAdvances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });
  const [total, setTotal] = useState(0);
  // Configured (dynamic) roles allowed to issue / settle advances.
  const [issuerRoles, setIssuerRoles] = useState(DEFAULT_ISSUER_ROLES);
  const [settlerRoles, setSettlerRoles] = useState(DEFAULT_SETTLER_ROLES);
  const [currencySymbol, setCurrencySymbol] = useState(getExpenseCurrencySymbol());
  // Settlement review modal - shows an advance's submitted expenses and their
  // bill attachments so the settler can verify before confirming settlement.
  const [settleReview, setSettleReview] = useState({
    open: false,
    advance: null,
    expenses: [],
    loading: false,
    submitting: false,
  });

  const canCreateAdvance = useMemo(
    () => getResource(config.RESOURCES.EXPENSE_ADVANCE, ["create"]),
    []
  );
  // Issue button: needs create permission AND a role the company allows to issue.
  const mayIssue = useMemo(
    () => canCreateAdvance && getRoles(issuerRoles),
    [canCreateAdvance, issuerRoles]
  );
  // Settle button: role must be a configured settler (Admin can always settle).
  const maySettle = useMemo(
    () => getRoles([...settlerRoles, "Admin"]),
    [settlerRoles]
  );
  // True when every expense loaded into the settlement review is Rejected -
  // settling here closes the advance with nothing approved (full amount goes
  // to recovery), so the settler gets an extra confirm before it happens.
  const allExpensesRejected = useMemo(
    () =>
      settleReview.expenses.length > 0 &&
      settleReview.expenses.every((e) => e.approvalStatus === "Rejected"),
    [settleReview.expenses]
  );

  const [isTourOpen, setIsTourOpen] = useState(false);
  const [isTourHintOpen, setIsTourHintOpen] = useState(false);

  // First-time visitors get a "click here" nudge pointing at the guide
  // trigger. It stays up until dismissed, which marks it done via its own
  // storage key so it never comes back.
  useEffect(() => {
    if (hasTourFlag(TOUR_STORAGE_KEY) || hasTourFlag(TOUR_HINT_STORAGE_KEY)) return;
    const showTimer = setTimeout(() => setIsTourHintOpen(true), 600);
    return () => clearTimeout(showTimer);
  }, []);

  // Once the tour walks past the "+ Advance" button, it opens the real Issue
  // Advance modal so the remaining steps can point at its actual fields - and
  // closes it again once the tour leaves those steps.
  const handleTourStepChange = useCallback((step) => {
    setIsModalOpen(Boolean(step && MODAL_STEP_IDS.includes(step.id)));
  }, []);

  const loadAdvances = useCallback(async () => {
    dispatch(showAuthLoader());
    try {
      const data = await expenseApi.get(Service.expenseAdvance, {
        page: pagination.current,
        limit: pagination.pageSize,
      });
      setAdvances(data?.items || []);
      setTotal(data?.total || 0);
    } catch (err) {
      message.error(err.message || "Failed to load advances");
    } finally {
      dispatch(hideAuthLoader());
    }
  }, [dispatch, pagination.current, pagination.pageSize]);

  const loadReferenceData = useCallback(async () => {
    try {
      const [emps, centers] = await Promise.all([
        fetchEmployeeOptions(),
        expenseApi.get(Service.expenseCostCenter).catch(() => ({ items: [] })),
      ]);
      setEmployees(emps);
      setCostCenters(centers?.items || centers || []);
    } catch (err) {
      message.error(err.message || "Failed to load reference data");
    }
  }, []);

  const loadRoleConfig = useCallback(async () => {
    try {
      const cfg = await expenseApi.get(Service.expenseConfig);
      setIssuerRoles(cfg?.issuerRoles?.length ? cfg.issuerRoles : DEFAULT_ISSUER_ROLES);
      setSettlerRoles(cfg?.settlerRoles?.length ? cfg.settlerRoles : DEFAULT_SETTLER_ROLES);
      const sym = cfg?.currencySymbol || "₹";
      setExpenseCurrencySymbol(sym);
      setCurrencySymbol(sym);
    } catch {
      /* keep defaults */
    }
  }, []);

  useEffect(() => {
    loadAdvances();
  }, [loadAdvances]);

  useEffect(() => {
    loadRoleConfig();
  }, [loadRoleConfig]);

  useEffect(() => {
    if (mayIssue) loadReferenceData();
  }, [mayIssue, loadReferenceData]);

  const openModal = () => {
    form.resetFields();
    form.setFieldsValue({ dateIssued: moment() });
    setIsModalOpen(true);
  };

  const handleIssue = async (values) => {
    dispatch(showAuthLoader());
    try {
      const body = {
        employeeId: values.employeeId,
        advanceAmount: values.advanceAmount,
        purpose: values.purpose,
        projectClient: values.projectClient || null,
        costCenterId: values.costCenterId || null,
        dateIssued: values.dateIssued
          ? values.dateIssued.format("YYYY-MM-DD")
          : undefined,
        expectedSettlementDate: values.expectedSettlementDate
          ? values.expectedSettlementDate.format("YYYY-MM-DD")
          : undefined,
        remarks: values.remarks || null,
      };
      await expenseApi.post(Service.expenseAdvance, body);
      message.success("Advance issued successfully");
      setIsModalOpen(false);
      loadAdvances();
    } catch (err) {
      message.error(err.message || "Failed to issue advance");
    } finally {
      dispatch(hideAuthLoader());
    }
  };

  // Public URL for an expense bill attachment (served statically by the API).
  const attachmentUrl = (filename) =>
    `${Service.Server_Base_URL}/uploads/expenses/${filename}`;

  // Open the settlement review - loads the advance's submitted expenses so the
  // settler can inspect each expense and its bill attachments before confirming.
  const openSettleReview = async (advance) => {
    setSettleReview({ open: true, advance, expenses: [], loading: true, submitting: false });
    try {
      const data = await expenseApi.get(Service.expense, {
        advanceId: advance._id,
        limit: 200,
      });
      setSettleReview((prev) => ({ ...prev, expenses: data?.items || [], loading: false }));
    } catch (err) {
      message.error(err.message || "Failed to load expenses for this advance");
      setSettleReview((prev) => ({ ...prev, loading: false }));
    }
  };

  const closeSettleReview = () =>
    setSettleReview({ open: false, advance: null, expenses: [], loading: false, submitting: false });

  const confirmSettlement = async () => {
    if (!settleReview.advance) return;
    setSettleReview((prev) => ({ ...prev, submitting: true }));
    try {
      const result = await expenseApi.put(
        `${Service.expenseAdvance}/${settleReview.advance._id}/settle`,
        {}
      );
      const finalStatus = result?.advance?.status;
      const openLeft = Number(result?.openExpenses || 0);
      if (finalStatus === "Closed") {
        message.success("Advance fully settled and closed.");
      } else if (openLeft > 0) {
        // Can't close while expenses are still pending/on hold - tell the settler why.
        message.warning({
          content: `Advance stays partially settled: ${openLeft} expense(s) are still pending/on hold. Approve or reject them in the Approval Queue to fully close this advance.`,
          duration: 6,
        });
      } else {
        message.success(`Advance status updated to ${finalStatus || "settled"}.`);
      }
      closeSettleReview();
      loadAdvances();
    } catch (err) {
      message.error(err.message || "Failed to settle advance");
      setSettleReview((prev) => ({ ...prev, submitting: false }));
    }
  };

  // Columns for the expenses table inside the settlement review modal.
  const settleExpenseColumns = [
    {
      title: "Date",
      dataIndex: "expenseDate",
      key: "expenseDate",
      render: (v) => (v ? moment(v).format(dateFormat) : "-"),
    },
    { title: "Category", dataIndex: "category", key: "category", render: (v) => v || "-" },
    { title: "Amount", dataIndex: "amount", key: "amount", render: (v) => formatMoney(v) },
    {
      title: "Status",
      dataIndex: "approvalStatus",
      key: "approvalStatus",
      render: (v) => statusTag(v),
    },
    {
      title: "Attachments",
      key: "attachments",
      render: (_, r) => {
        const files = r.attachments || [];
        if (!files.length) return <span className="expense-muted">-</span>;
        return (
          <Space direction="vertical" size={2}>
            {files.map((f, i) => (
              <a
                key={i}
                href={attachmentUrl(f)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {files.length > 1 ? `View bill ${i + 1}` : "View bill"}
              </a>
            ))}
          </Space>
        );
      },
    },
  ];

  const columns = [
    {
      title: "Advance ID",
      dataIndex: "advanceId",
      key: "advanceId",
      render: (v) => v || "-",
    },
    {
      title: "Employee",
      dataIndex: "employeeName",
      key: "employeeName",
      render: (text, record) => <EmpNameCodeCell name={record.employeeName} code={record.empCode} />,
    },
    {
      title: "Department",
      dataIndex: "departmentName",
      key: "departmentName",
      render: (v) => v || "-",
    },
    {
      title: "Designation",
      dataIndex: "designationName",
      key: "designationName",
      render: (v) => v || "-",
    },
    { title: "Grade", dataIndex: "salaryGrade", key: "salaryGrade", render: (v) => v || "-" },
    {
      title: "Amount",
      dataIndex: "advanceAmount",
      key: "advanceAmount",
      render: (v) => formatMoney(v),
    },
    { title: "Purpose", dataIndex: "purpose", key: "purpose", render: (v) => v || "-" },
    {
      title: "Date Issued",
      dataIndex: "dateIssued",
      key: "dateIssued",
      render: (v) => (v ? moment(v).format(dateFormat) : "-"),
    },
    {
      title: "Expected Settlement",
      dataIndex: "expectedSettlementDate",
      key: "expectedSettlementDate",
      render: (v) => (v ? moment(v).format(dateFormat) : "-"),
    },
    { title: "Status", dataIndex: "status", key: "status", render: (v) => statusTag(v) },
    {
      title: <span data-tour="ar-actions">Actions</span>,
      key: "actions",
      render: (_, record) => {
        // Show Settle only when the advance is still open (Issued/PartiallySettled)
        // AND every expense against it has been approved/rejected at all levels
        // (no Pending/Hold left) - so settling will actually close it.
        const notClosed = ["Issued", "PartiallySettled"].includes(record.status);
        const allApproved = Number(record.openExpenseCount || 0) === 0;
        const isOpen = notClosed && allApproved;
        return (
          <Space>
            {maySettle && isOpen && (
              <Button
                size="small"
                type="primary"
                className="filter-btn"
                onClick={() => openSettleReview(record)}
              >
                Settle
              </Button>
            )}
            {!isOpen && <span className="expense-muted">-</span>}
          </Space>
        );
      },
    },
  ];

  const tourSteps = useMemo(() => {
    const steps = [
      {
        target: '[data-tour="ar-table"]',
        title: "Advances at a glance",
        content: "Every advance issued, with status and settlement details.",
      },
    ];
    if (mayIssue) {
      steps.push(
        {
          id: "ar-add-btn",
          target: '[data-tour="ar-add-btn"]',
          title: "Issue a new advance",
          content: "Click here to issue an advance to an employee - opens a short form.",
        },
        {
          id: "ar-employee",
          target: '[data-tour="ar-employee"]',
          title: "Who it's for",
          content: "Required - the employee receiving this advance.",
        },
        {
          id: "ar-amount",
          target: '[data-tour="ar-amount"]',
          title: "How much",
          content: "Required.",
        },
        {
          id: "ar-cost-center",
          target: '[data-tour="ar-cost-center"]',
          title: "Cost center",
          content: "Optional.",
        },
        {
          id: "ar-purpose",
          target: '[data-tour="ar-purpose"]',
          title: "Why it's being issued",
          content: "Required.",
        },
        {
          id: "ar-project-client",
          target: '[data-tour="ar-project-client"]',
          title: "Project / Client",
          content: "Optional.",
        },
        {
          id: "ar-dates",
          target: '[data-tour="ar-dates"]',
          title: "Dates",
          content: "Expected Settlement Date must be after Date Issued - pick Date Issued first, or the settlement picker won't let you choose an earlier date.",
        },
        {
          id: "ar-remarks",
          target: '[data-tour="ar-remarks"]',
          title: "Remarks",
          content: "Optional.",
        },
        {
          id: "ar-issue-btn",
          target: '[data-tour="ar-issue-btn"]',
          title: "Issue",
          content: "Creates the advance - the employee can then submit expenses against it.",
        }
      );
    }
    if (maySettle) {
      steps.push({
        target: '[data-tour="ar-actions"]',
        title: "Settling an advance",
        content:
          "A Settle button appears here once every expense against an advance has been approved or rejected (nothing left pending). It opens a review of the expenses and bill attachments before you confirm - if every expense was rejected, you'll get one more confirmation since nothing would actually be paid out.",
      });
    }
    return steps;
  }, [mayIssue, maySettle]);

  const hintSteps = useMemo(
    () => [
      {
        target: '[data-tour="ar-guide-icon"]',
        shape: "circle",
        title: "New here?",
        content: "Click this icon anytime for a quick, step-by-step guide to this screen.",
      },
    ],
    []
  );

  return (
    <Card className="employee-card expense-page">
      <div className="inout-header text-left">
        <h2>
          Advance Requests{" "}
          <QuestionCircleOutlined
            data-tour="ar-guide-icon"
            className="guide-title-icon"
            role="button"
            tabIndex={0}
            onClick={() => {
              setIsTourHintOpen(false);
              setIsTourOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setIsTourHintOpen(false);
                setIsTourOpen(true);
              }
            }}
            title="Click for a step-by-step guide"
          />
        </h2>
        <div className="header-btns">
          {mayIssue && (
            <Tooltip title="Issue a new advance">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openModal}
                data-tour="ar-add-btn"
              >
                Advance
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
      <Card className="main-content-wrapper">
        <div className="main-table-wrapper" data-tour="ar-table">
          <Table
            rowKey="_id"
            columns={columns}
            dataSource={advances}
            locale={{ emptyText: <NodataFoundIcon /> }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              onChange: (current, pageSize) => setPagination({ current, pageSize }),
            }}
            scroll={{ x: true }}
          />
        </div>
      </Card>

      <Modal
        title="Issue Advance"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        okText="Issue"
        okButtonProps={{ "data-tour": "ar-issue-btn" }}
        destroyOnClose
        width={640}
      >
        <Form form={form} layout="vertical" onFinish={handleIssue}>
          <Row gutter={16}>
            <Col span={24} data-tour="ar-employee">
              <Form.Item
                name="employeeId"
                label="Employee"
                rules={[{ required: true, message: "Select an employee" }]}
              >
                <Select
                  showSearch
                  placeholder="Select employee"
                  optionFilterProp="children"
                >
                  {employees.map((emp) => (
                    <Option key={emp._id} value={emp._id}>
                      {emp.full_name || emp.name || emp.email}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12} data-tour="ar-amount">
              <Form.Item
                name="advanceAmount"
                label="Amount"
                rules={[{ required: true, message: "Enter an amount" }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  prefix={<span style={{ marginRight: 4 }}>{currencySymbol}</span>}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col span={12} data-tour="ar-cost-center">
              <Form.Item name="costCenterId" label="Cost Center">
                <Select allowClear placeholder="Select cost center">
                  {costCenters.map((cc) => (
                    <Option key={cc._id} value={cc._id}>
                      {cc.name}
                      {cc.code ? ` (${cc.code})` : ""}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="purpose"
            label="Purpose"
            rules={[{ required: true, message: "Enter the purpose" }]}
            data-tour="ar-purpose"
          >
            <Input placeholder="Purpose of advance" />
          </Form.Item>
          <Form.Item name="projectClient" label="Project / Client" data-tour="ar-project-client">
            <Input placeholder="Project or client name" />
          </Form.Item>
          <Row gutter={16} data-tour="ar-dates">
            <Col span={12}>
              <Form.Item name="dateIssued" label="Date Issued">
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  onChange={(issued) => {
                    // Clear the settlement date if it is now on/before the issue date.
                    const settlement = form.getFieldValue("expectedSettlementDate");
                    if (issued && settlement && settlement.isSameOrBefore(issued, "day")) {
                      form.setFieldsValue({ expectedSettlementDate: null });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="expectedSettlementDate"
                label="Expected Settlement Date"
                dependencies={["dateIssued"]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  // Settlement must be AFTER Date Issued - disable that day and earlier.
                  disabledDate={(current) => {
                    const issued = form.getFieldValue("dateIssued");
                    return !!(
                      current &&
                      issued &&
                      current.isSameOrBefore(moment(issued), "day")
                    );
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remarks" label="Remarks" data-tour="ar-remarks">
            <TextArea rows={2} placeholder="Optional remarks" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Settle Advance${
          settleReview.advance ? `: ${settleReview.advance.advanceId}` : ""
        }`}
        open={settleReview.open}
        onCancel={closeSettleReview}
        width={780}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={closeSettleReview}>
            Cancel
          </Button>,
          allExpensesRejected ? (
            <Popconfirm
              key="settle"
              title="All expenses on this advance are rejected"
              description={`${settleReview.expenses.length} expense(s) linked to this advance were all rejected, so nothing will be approved for reimbursement. Do you still want to settle this advance?`}
              okText="Yes, settle anyway"
              cancelText="Cancel"
              onConfirm={confirmSettlement}
            >
              <Button type="primary" loading={settleReview.submitting}>
                Confirm Settlement
              </Button>
            </Popconfirm>
          ) : (
            <Button
              key="settle"
              type="primary"
              loading={settleReview.submitting}
              onClick={confirmSettlement}
            >
              Confirm Settlement
            </Button>
          ),
        ]}
      >
        {settleReview.advance && (
          <div className="expense-settle-summary">
            <span>
              <strong>Employee:</strong>{" "}
              {settleReview.advance.employeeName || "-"}
              {settleReview.advance.empCode ? ` (${settleReview.advance.empCode})` : ""}
            </span>
            <span>
              <strong>Advance:</strong>{" "}
              {formatMoney(settleReview.advance.advanceAmount)}
            </span>
          </div>
        )}
        <p className="expense-muted" style={{ margin: "4px 0 8px" }}>
          Review the submitted expenses and their bill attachments before settling.
          Settlement is a state change only (no payout).
        </p>
        {(() => {
          const openLeft = (settleReview.expenses || []).filter(
            (e) => !["Approved", "Rejected"].includes(e.approvalStatus)
          ).length;
          return openLeft > 0 ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 8 }}
              message={`${openLeft} expense(s) are still pending / on hold.`}
              description="Settling now will keep the advance Partially Settled. Approve or reject the open expenses in the Approval Queue to fully close it."
            />
          ) : null;
        })()}
        <Table
          rowKey="_id"
          size="small"
          loading={settleReview.loading}
          columns={settleExpenseColumns}
          dataSource={settleReview.expenses}
          pagination={false}
          scroll={{ x: "max-content", y: 320 }}
          locale={{ emptyText: "No expenses submitted against this advance." }}
        />
      </Modal>

      {isTourOpen && (
        <GuidedTour
          steps={tourSteps}
          onClose={() => setIsTourOpen(false)}
          onStepChange={handleTourStepChange}
          storageKey={TOUR_STORAGE_KEY}
        />
      )}

      {!isTourOpen && isTourHintOpen && (
        <GuidedTour steps={hintSteps} onClose={() => setIsTourHintOpen(false)} storageKey={TOUR_HINT_STORAGE_KEY} />
      )}
    </Card>
  );
};

export default AdvanceRequest;
